<template>
  <div ref="numericFieldHolder" class="numeric-field-holder" :style="getElementColor">
    <div class="label-value-holder" :class="getLayout">
      <div v-show="!noLabel" class="num-label-numeric" :style="getColor">{{ getLabel }}</div>
      <div v-show="getIsOffset" class="offset-marker">{{ getOffsetLetter }}</div>
      <input
        id="inputId"
        type="text"
        ref="input"
        class="num-value"
        :style="getBgColor"
        @click.stop="handleValueClick"
        v-model.lazy ="numericValue"
        @focusin="handleFocusIn"
        @focusout="handleFocusOut"
        @keydown.enter="handleEnterKey"
      >
    </div>
    <div class="numeric-unit" :style="getColor">{{ getUnit }}</div>
    <IncDecButtons @inc="handleIncDec(1)" @dec="handleIncDec(-1)" :class="getEditState" layout="row" :buttonColor="buttonColor"/>
    <NumericEntry ref="numeric" v-show="this.showNumeric" @hide="handleHide" :segment="segment"/>
  </div>
</template>

<script>
import IncDecButtons from '@/components/nelson/IncDecButtons';
import NumericEntry from '@/components/nelson/NumericEntry';
import { useInputStore } from '@/store/InputStore';
import { usePipeStore } from '@/store/PipeStore';
import { useSegmentStore } from '@/store/SegmentStore';
import { useSettingsStore } from '@/store/SettingsStore';
import * as UnitConversion from '@/services/UnitConversionService';
// import { useModalStore } from '@/store/ModalStore';

export default {
  name: 'NumericField',
  props: [
    'segment',
    'type',
    'obj',
    'bgColor',
    'limit',
    'layout',
    'color',
    'buttonColor',
    'elementColor',
    'noLabel'
  ],
  data () {
    return {
      showNumeric: false,
      hasFocus: false,
      dataInput: null,
    };
  },
  computed: {
    getIsOffset () {
      return this.type.indexOf('offset') > -1;
    },
    getOffsetLetter () {
      if (this.type === 'offset l') {
        return 'L';
      }
      if (this.type === 'offset r') {
        return 'R';
      }
    },
    getElementColor () {
      return 'background-color: ' + (this.elementColor ? this.elementColor : '') + ';';
    },
    getColor () {
      return 'color: ' + (this.color ? this.color : 'black') + ';';
    },
    getLayout () {
      return this.layout === 'row' ? 'nf-row' : '';
    },
    segmentLength: {
      get () {
        return this.filterLength(this.$props.segment.value);
      },
      set (value) {
        this.$props.segment.value = Math.min(Math.max(0, this.unfilterLength(value)), 1610);
        useSegmentStore().updateSlopes();
        useInputStore().sprinklerCalc();
      }
    },
    inletPressure: {
      get () {
        return this.filterPressure(useSegmentStore().getInletPressure);
      },
      set (value) {
        useSegmentStore().setInletPressure(this.unfilterPressure(value));
        useInputStore().sprinklerCalc();
      }
    },
    inletPosition: {
      get () {
        return this.filterLength(useSegmentStore().getInletPosition);
      },
      set (value) {
        const segmentStore = useSegmentStore();
        segmentStore.setInletPosition(this.unfilterLength(value));
        segmentStore.splitInlet();
        useInputStore().sprinklerCalc();
      }
    },
    sprinklerSpacing: {
      get () {
        return this.filterLength(useInputStore().getSprinklerSpacing);
      },
      set (value) {
        const inputStore = useInputStore();
        inputStore.setSprinklerSpacing(this.unfilterLength(value));
        inputStore.sprinklerCalc();
      }
    },
    leftOffset: {
      get () {
        return this.filterLength(useInputStore().getLeftOffset);
      },
      set (value) {
        const inputStore = useInputStore();
        inputStore.setLeftOffset(this.unfilterLength(value));
        inputStore.sprinklerCalc();
      }
    },
    rightOffset: {
      get () {
        return this.filterLength(useInputStore().getRightOffset);
      },
      set (value) {
        const inputStore = useInputStore();
        inputStore.setRightOffset(this.unfilterLength(value));
        inputStore.sprinklerCalc();
      }
    },
    pipeDiameter: {
      get () {
        return this.filterDiameter(usePipeStore().getEditedPipe[this.$props.obj.value]);
      },
      set (value) {
        usePipeStore().getEditedPipe[this.$props.obj.value] = this.unfilterDiameter(value);
      }
    },
    slopeStart: {
      get () {
        return this.filterSlope(this.$props.segment.value, useSegmentStore().getSlopeStart);
      },
      set (value) {
        useSegmentStore().setSlopeStart(this.unfilterSlope(this.$props.segment.value, value));
      }
    },
    slope: {
      get () {
        return this.filterSlope(this.$props.segment.value, this.$props.segment.slope);
      },
      set (value) {
        this.$props.segment.slope = this.unfilterSlope(this.$props.segment.value, value);
        useInputStore().sprinklerCalc();
      }
    },
    rangeMarkerValue: {
      get () {
        const type = this.$props.obj.value;
        const limit = this.$props.limit;
        const settingsStore = useSettingsStore();
        if (type == 'sprinklerPressure' && limit == 'min') { return this.filterPressure(settingsStore.getMinSprinklerPressure()); }
        if (type == 'sprinklerPressure' && limit == 'max') { return this.filterPressure(settingsStore.getMaxSprinklerPressure()); }
        if (type == 'sprinklerFlow' && limit == 'min') { return this.filterEmitterFlow(settingsStore.getMinSprinklerFlow()); }
        if (type == 'sprinklerFlow' && limit == 'max') { return this.filterEmitterFlow(settingsStore.getMaxSprinklerFlow()); }
        if (type == 'pipePressure' && limit == 'min') { return this.filterPressure(settingsStore.getMinPipePressure()); }
        if (type == 'pipePressure' && limit == 'max') { return this.filterPressure(settingsStore.getMaxPipePressure()); }
        if (type == 'velocity' && limit == 'min') { return this.filterVelocity(settingsStore.getMinVelocity()); }
        if (type == 'velocity' && limit == 'max') { return this.filterVelocity(settingsStore.getMaxVelocity()); }
        return null;
      },
      set (value) {
        const type = this.$props.obj.value;
        const limit = this.$props.limit;
        const settingsStore = useSettingsStore();
        if (type == 'sprinklerPressure' && limit == 'min') { settingsStore.setMinSprinklerPressure(this.unfilterPressure(value)); }
        if (type == 'sprinklerPressure' && limit == 'max') { settingsStore.setMaxSprinklerPressure(this.unfilterPressure(value)); }
        if (type == 'sprinklerFlow' && limit == 'min') { settingsStore.setMinSprinklerFlow(this.unfilterEmitterFlow(value)); }
        if (type == 'sprinklerFlow' && limit == 'max') { settingsStore.setMaxSprinklerFlow(this.unfilterEmitterFlow(value)); }
        if (type == 'pipePressure' && limit == 'min') { settingsStore.setMinPipePressure(this.unfilterPressure(value)); }
        if (type == 'pipePressure' && limit == 'mix') { settingsStore.setMaxPipePressure(this.unfilterPressure(value)); }
        if (type == 'velocity' && limit == 'min') { settingsStore.setMinVelocity(this.unfilterVelocity(value)); }
        if (type == 'velocity' && limit == 'max') { settingsStore.setMaxVelocity(this.unfilterVelocity(value)); }
      }
    },
    numericValue: {
      get () {
        switch (this.$props.type) {
          case 'graph':
            return this.rangeMarkerValue.toFixed(2);
          case 'edit':
            return this.pipeDiameter.toFixed(4);
          case 'origin':
            return this.inletPosition.toFixed(2);
          case 'pressure':
            return this.inletPressure.toFixed(2);
          case 'spacing':
            return this.sprinklerSpacing.toFixed(2);
          case 'offset l':
            return this.leftOffset.toFixed(2);
          case 'offset r':
            return this.rightOffset.toFixed(2);
          case 'start-gradient':
            return this.slopeStart.toFixed(2);
          case 'gradient':
            return this.slope.toFixed(2);
          default:
            return this.hasFocus ? this.segmentLength : this.segmentLength.toFixed(1);
        }
      },
      set (value) {
        this.handleChange(parseFloat(value));
      }
    },
    inputId () {
      return this.formatToken('nf-input-' + (this.$props.type != 'pipe' ? this.$props.type : this.$props.segment.type));
    },
    getBgColor () {
      return 'background-color:' + this.$props.bgColor + ';';
    },
    getUnit () {
      const settingsStore = useSettingsStore();
      switch (this.$props.type) {
        case 'graph':
          if (this.$props.obj.value == 'sprinklerFlow') { return settingsStore.getEmitterFlowUnit; }
          if (this.$props.obj.value == 'velocity') { return settingsStore.getVelocityUnit; }
          return settingsStore.getPressureUnit;
        case 'gradient':
        case 'start-gradient':
          return settingsStore.getSlopeUnit;
        case 'edit':
          return settingsStore.getDiameterUnit;
        case 'pressure':
          return settingsStore.getPressureUnit;
        case 'flow':
          return settingsStore.getEmitterFlowUnit;
        case 'velocity':
          return settingsStore.getVelocityUnit;
        default:
          return settingsStore.getLengthUnit;
      }
    },
    getLabel () {
      switch (this.$props.type) {
        case 'graph':
          return this.$props.limit + ' marker';
        case 'slope':
        case 'pipe':
          return 'length';
        case 'gradient':
          return 'slope';
        case 'start-gradient':
          return 'start';
        case 'edit':
          return 'Diameter';
        case 'origin':
          return 'location';
        case 'pressure':
          return 'pressure';
        case 'spacing':
          return 'spacing';
        case 'offset l':
          return 'offset';
        case 'offset r':
          return 'offset';
      }
    },
    getIncDecValue () {
      if (this.getUnit == 'gpm') { return 0.1; }
      if (this.getUnit == 'lpm') { return 0.01; }
      if (this.getUnit == 'bar') { return 0.1; }
      return 1;
    },
    getEditState () {
      return this.$props.type !== 'edit' ? '' : (usePipeStore().getAllowPipeEdit ? 'editable' : 'not-editable');
    },
  },
  components: {
    IncDecButtons,
    NumericEntry
  },
  methods: {
    formatToken (token) {
      if (token == null) { return token; }
      return token.replace(/\s|\.|\//g, '-').replace(/#/g, '');
    },
    filterLength (length) {
      return UnitConversion.FilterLength(length, useSettingsStore().getLengthUnit);
    },
    filterDiameter (diameter) {
      return UnitConversion.FilterDiameter(diameter, useSettingsStore().getDiameterUnit);
    },
    filterPressure (pressure) {
      return UnitConversion.FilterPressure(pressure, useSettingsStore().getPressureUnit);
    },
    filterSlope (pipeLength, slope) {
      return UnitConversion.FilterSlope(pipeLength, slope, useSettingsStore().getSlopeUnit);
    },
    filterEmitterFlow (flow) {
      return UnitConversion.FilterEmitterFlow(flow, useSettingsStore().getEmitterFlowUnit);
    },
    filterVelocity (velocity) {
      return UnitConversion.FilterFlow(velocity, useSettingsStore().getVelocityUnit);
    },
    unfilterDiameter (length) {
      return UnitConversion.UnfilterDiameter(length, useSettingsStore().getDiameterUnit);
    },
    unfilterLength (length) {
      return UnitConversion.UnfilterLength(length, useSettingsStore().getLengthUnit);
    },
    unfilterPressure (pressure) {
      return UnitConversion.UnfilterPressure(pressure, useSettingsStore().getPressureUnit);
    },
    unfilterEmitterFlow (flow) {
      return UnitConversion.UnfilterEmitterFlow(flow, useSettingsStore().getEmitterFlowUnit);
    },
    unfilterVelocity (velocity) {
      return UnitConversion.UnfilterVelocity(velocity, useSettingsStore().getVelocityUnit);
    },
    unfilterSlope (pipeLength, slope) {
      return UnitConversion.UnfilterSlope(pipeLength, slope, useSettingsStore().getSlopeUnit);
    },
    handleIncDec (val) {
      const adjustment = this.getIncDecValue * val;
      switch (this.$props.type) {
        case 'pressure':
          this.inletPressure = Math.max(0, this.inletPressure + adjustment);
          return;
        case 'graph':
          this.rangeMarkerValue = Math.max(0, this.rangeMarkerValue + adjustment);
          return;
        case 'edit':
          this.pipeDiameter = Math.max(0, this.pipeDiameter + adjustment);
          return;
        case 'origin':
          this.inletPosition = Math.max(0, this.inletPosition + adjustment);
          return;
        case 'spacing':
          this.sprinklerSpacing = Math.max(0, this.sprinklerSpacing + adjustment);
          return;
        case 'offset l':
          if (this.leftOffset + adjustment < 0) { return; }
          this.updateOffset('left', adjustment);
          return;
        case 'offset r':
          if (this.rightOffset + adjustment < 0) { return; }
          this.updateOffset('right', adjustment);
          return;
        case 'gradient':
          this.slope = this.slope + adjustment;
          return;
        case 'start-gradient':
          this.slopeStart = this.slopeStart + adjustment;
          return;
        default:
          this.segmentLength += adjustment;
          return;
      }
    },
    updateOffset (type, val) {
      const inputStore = useInputStore();
      inputStore.lockOffsets();
      if (type === 'left') {
        this.leftOffset += val;
      } else {
        this.rightOffset += val;
      }
      inputStore.unlockOffsets();
    },
    handleValueClick () {

      // todo: position of numeric entry for non segment inputs

      this.$refs.numeric.$el.style.left = (this.$el.getBoundingClientRect().left - 50) + 'px';
      this.$refs.input.select();
      document.body.addEventListener('mousedown', this.handleMouseDown);
    },
    handleMouseDown (e) {
      if (!e.target.classList.contains('numeric-entry-button-holder')) { this.showNumeric = false; }
      document.body.removeEventListener('mousedown', this.handleMouseDown);
    },
    handleHide () {
      this.showNumeric = false;
    },
    handleFocusIn () {
      this.hasFocus = true;
      // if (this.$props.type == 'pipe' || this.$props.type == 'slope') { this.showNumeric = true; }
      window.addEventListener('wheel', this.handleWheel);
    },
    handleWheel (e) {
      this.handleIncDec(e.deltaY < 0 ? 1 : -1);
    },
    handleChange (value) {
      const convertedValue = isNaN(value) ? 0 : value;
      switch (this.$props.type) {
        case 'edit':
          this.pipeDiameter = convertedValue;
          break;
        case 'graph':
          this.rangeMarkerValue = convertedValue;
          break;
        case 'origin':
          this.inletPosition = Math.max(value, 0);
          break;
        case 'pressure':
          this.inletPressure = value;
          break;
        case 'spacing':
          this.sprinklerSpacing = Math.max(value, 0.01);
          break;
        case 'offset l':
          this.leftOffset = Math.max(value, 0);
          break;
        case 'offset r':
          this.rightOffset = Math.max(value, 0);
          break;
        case 'start-gradient':
          this.slopeStart = value;
          break;
        case 'gradient':
          this.slope = value;
          return;
        default:
          this.segmentLength = value;
          break;
      }
    },
    handleEnterKey (e) {
      e.target.blur();
      this.handleHide();
      window.removeEventListener('wheel', this.handleWheel);
    },
    handleFocusOut () {
      this.hasFocus = false;
      window.removeEventListener('wheel', this.handleWheel);
    },
  }
};
</script>

<style scoped>
.nf-row {
  flex-direction: row !important;
}
.numeric-field-holder {
  display: flex;
  justify-content: flex-start;
  padding-left: 10px;
  gap: 6px;
  padding-top: 3px;
  padding-bottom: 3px;
  align-items: center;
}
.num-value {
  max-width: 60px;
  text-align: center;
  border: none;
  border-radius: 16px;
  font-weight: 700;
  font-family: "Heebo", sans-serif;
}
.num-value::selection {
  color: white;
  background: blue;
}
.editable {
  pointer-events: auto;
}
.not-editable {
  position: static;
  z-index: none;
  pointer-events: none;
}
.num-label-numeric {
  font-size: 11px;
  color: #363636;
  margin-right: 6px;
}
.label-value-holder {
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
}
.numeric-unit {
  font-weight: 900;
  font-size: 9px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-transform: uppercase;
}
.offset-marker {
  font-weight: 900;
  font-size: 10px;
  background-color: gray;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 50%;
}
</style>